import React, { PropsWithChildren, useRef } from 'react';
import classNames from 'classnames';
import Badge from '../Contact/Badges/Badge';
import ClayUserBadge from '../Contact/Badges/ClayUser';
import Occasion from '@support/Occasion';
import { BadgeStarredIcon, NewContactIcon, NewContactWithTextIcon } from '@components/Icons';
import { CSSTransition, SwitchTransition } from 'react-transition-group';

import styles from './ContactCard.module.scss';

import importAllFiles from '@support/importAllFiles';
import { useTheme } from '@hooks/use-theme';
import { ID } from '@app/types';
import { useSelector } from 'react-redux';
import { isStarredSelector } from '@actions/starred';
import Contact from '@models/Contact';

const icons = importAllFiles(import.meta.glob('/public/img/contact/icons/*.svg'));
const iconImages = importAllFiles(import.meta.glob('/public/img/contact/icons/*.png'));
const interactions = importAllFiles(import.meta.glob('/public/img/contact/icons/source/*.svg'));
const interactionImages = importAllFiles(
  import.meta.glob('/public/img/contact/icons/source/*.png'),
);
const badges = importAllFiles(import.meta.glob('/public/img/contact/*.svg'));

const StarredStatus = ({ contactID }: { contactID: ID }) => {
  const starred = useRef(null);
  const notStarred = useRef(null);
  const contactIsStarred = useSelector(isStarredSelector({ id: contactID } as Contact));
  const nodeRef = contactIsStarred ? starred : notStarred;

  return (
    <SwitchTransition mode="in-out">
      <CSSTransition
        key={String(contactIsStarred)}
        nodeRef={nodeRef}
        addEndListener={(done) => {
          nodeRef.current.addEventListener('transitionend', done, false);
        }}
        classNames={{
          enter: styles.badgeEnter,
          enterActive: styles.badgeEnterActive,
          exit: styles.badgeExit,
          exitActive: styles.badgeExitActive,
        }}
      >
        <span ref={nodeRef} className={styles.starredBadgeWrapper}>
          <span
            className={classNames(
              styles.starredBadge,
              contactIsStarred ? styles.starred : styles.notStarred,
            )}
          >
            {contactIsStarred && <BadgeStarredIcon className={styles.starred} />}
          </span>
        </span>
      </CSSTransition>
    </SwitchTransition>
  );
};

export function Relationship({
  relationship = null,
  isNew = false,
  icon = false,
  image: useImage = false,
  ...props
}) {
  const [_, themeFamily] = useTheme();

  if (!relationship && isNew === false) {
    return null;
  }

  let images = icon ? icons : badges;

  if (useImage && icon) {
    images = iconImages;
  }

  if (isNew) {
    relationship = 'new';
  }

  // themed new contact icons
  if (isNew && !icon && !useImage) {
    return <NewContactWithTextIcon className={props.className} />;
  } else if (isNew && icon && !useImage) {
    return <NewContactIcon className={props.className} />;
  }

  if (relationship !== 'new') {
    return null;
  }

  const imageKey = `${relationship}_${themeFamily}`;

  const image = images[imageKey] || images[relationship];
  return <Badge src={image} {...props} icon={icon} />;
}

export function InteractionAccesory({ interaction = null, image: useImage = false, ...props }) {
  const [_, themeFamily] = useTheme();

  if (!interaction) {
    return null;
  }

  const imageMap = useImage ? interactionImages : interactions;
  const imageKey = `${interaction}_${themeFamily}`;
  const image = imageMap[imageKey] || imageMap[interaction];

  return <Badge src={image} icon {...props} />;
}

function RelationshipAccessory({ contact, ...props }) {
  let created = null;

  if (contact.objectID) {
    created = Occasion.fromTimestamp(contact.created);
  } else {
    created = new Occasion(contact.created);
  }

  return (
    <Relationship {...props} isNew={created.isWithin(7)} relationship={contact.relationship} />
  );
}

const IconsAccessoryWrapper = ({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) => (
  <span className={classNames(styles.accessory, styles.icons, className)}>{children}</span>
);

export function IconsAccesory({
  contact,
  className = null,
  image = false,
  interaction = true,
  relationshipFull = false,
  children = null,
  noWrapper = false,
}) {
  let Wrapper = IconsAccessoryWrapper;
  let props: { className?: string } = { className };

  if (noWrapper) {
    Wrapper = React.Fragment;
    props = {};
  }

  return (
    <Wrapper {...props}>
      <StarredStatus contactID={contact.id} />
      <RelationshipAccessory image={image} contact={contact} icon={!relationshipFull} />
      {!interaction ? null : (
        <InteractionAccesory
          image={image}
          interaction={contact.interactionType || contact.interaction_type}
          icon
        />
      )}
      <ClayUserBadge image={image} contact={contact} icon />
      {children}
    </Wrapper>
  );
}
